/**
 * User management API types
 */

import type { Role, Status } from '@pillar-portal/database/dist/frontend';
import type {
    PermissionsGrant,
    RoleAssignmentDetailed,
    RoleAssignmentPermissionContext,
    RoleAssignmentPermissionsView,
} from './permission.types';
import type {
    PhysicalTerminalAccessExpectation,
    RoleAssignmentCustomConfig,
} from './backend_config.types';

// Discriminator: delivery_outcome
export type LoginInvitationDelivery =
    | {
          delivery_outcome: 'not_attempted';
      }
    | {
          delivery_outcome: 'provider_accepted';
          attempted_at: string;
      }
    | {
          delivery_outcome: 'failed';
          attempted_at: string;
      }
    | { delivery_outcome: 'unconfirmed'; attempted_at: string };

// Discriminator: activation_state
export type ProvisionedUser =
    | {
          activation_state: 'pending_first_login';
          user_uuid: string;
          first_login_expires_at: string;
          delivery: LoginInvitationDelivery;
      }
    | {
          activation_state: 'active_verification_bypassed';
          user_uuid: string;
          delivery: LoginInvitationDelivery;
      };

export interface LoginInvitationAdministrationState {
    user_uuid: string;
    user_status: 'PENDING';
    self_service_revoked: boolean;
    current_invitation_expires_at: string | null;
    delivery: LoginInvitationDelivery;
}

export interface ResendAllLoginInvitationsRequest {
    cursor?: string;
    limit?: number;
}

export interface ResendAllLoginInvitationsPage {
    results: Array<
        | {
              result_kind: 'resent';
              invitation: LoginInvitationAdministrationState;
          }
        | {
              result_kind: 'failed';
              user_uuid: string;
          }
    >;
    resent_count: number;
    failed_count: number;
    next_cursor?: string;
    unconfirmed_count: number;
}

export function countAcceptedLoginInvitationDeliveries(
    results: ResendAllLoginInvitationsPage['results']
): number {
    return results.filter(
        (result) =>
            result.result_kind === 'resent' &&
            result.invitation.delivery.delivery_outcome === 'provider_accepted'
    ).length;
}

export function countUnconfirmedLoginInvitationDeliveries(
    results: ResendAllLoginInvitationsPage['results']
): number {
    return results.filter(
        (result) =>
            result.result_kind === 'resent' &&
            result.invitation.delivery.delivery_outcome === 'unconfirmed'
    ).length;
}

/**
 * Unified request for provisioning users.
 * organization_id is required for OUser, optional/null for IUser
 */
export interface ProvisionUserRequest {
    primary_email: string;
    emails: string[];
    role: Role;
    organization_id: number | null; // Required for organizational roles, null/undefined for internal users
    first_name: string | null;
    last_name: string | null;
    skip_verification?: boolean;
    policies?: string[];
}

/**
 * Response from provisioning a user.
 * Contains the created user, invitation token, and expiration.
 */
export interface ProvisionUserResponse {
    user: UserProfileDetailedResponse | null;
    invitation_token: string | null;
    invitation_expires_at: string | null; // ISO date string
    primary_email: string;
}

export interface ProvisionInternalUserCreatedUser {
    user_uuid: string;
}

export interface ProvisionInternalUserResponse {
    user: ProvisionInternalUserCreatedUser | null;
    invitation_token: string | null;
    invitation_expires_at: string | null; // ISO date string
    primary_email: string;
}

export interface ProvisionOrganizationalUserRequest {
    primary_email: string;
    emails: string[];
    role: Role;
    organization_uuid: string;
    first_name: string | null;
    last_name: string | null;
    skip_verification?: boolean;
    policies?: string[];
}

export interface ProvisionOrganizationalUserCreatedUser {
    user_uuid: string;
}

export interface ProvisionOrganizationalUserResponse {
    user: ProvisionOrganizationalUserCreatedUser | null;
    invitation_token: string | null;
    invitation_expires_at: string | null; // ISO date string
    primary_email: string;
}

/**
 * Request to update a user
 */
export interface UpdateUserRequest {
    primary_email?: string;
    status?: Status;
    first_name?: string | null;
    last_name?: string | null;
}

export interface UpdateUserResponse {
    user_uuid: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    emails: string[];
    phones?: string[];
    primary_phone?: string | null;
    totp_enabled: boolean;
    status: Status;
    invitation_token?: string;
}

/**
 * User profile response with full details
 */
export interface UserProfileResponse {
    user_id: number;
    user_uuid?: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    emails: string[];
    phones?: string[];
    primary_phone?: string | null;
    totp_enabled: boolean;
    status: Status;
    invitation_token?: string;
}
export interface UserProfileDetailedResponse extends UserProfileResponse {
    default_role_assignment: RoleAssignmentDetailed | null;
    other_role_assignments: RoleAssignmentDetailed[];
}

export interface UserProfilePublicResponse {
    user_uuid: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    /** Verified email identities eligible for authentication. */
    emails: string[];
    unverified_emails: string[];
    pending_primary_email: string | null;
    /** Verified phone identities eligible for authentication. */
    phones?: string[];
    unverified_phones: string[];
    primary_phone?: string | null;
    pending_primary_phone: string | null;
    totp_enabled: boolean;
    mfa_required: boolean;
    mfa_email_allowed: boolean;
    mfa_sms_allowed: boolean;
    status: Status;
    login_invitation: LoginInvitationAdministrationState | null;
    /** Retired bearer field retained only to keep pre-migration readers type-safe. */
    invitation_token?: never;
}

export interface RoleAssignmentsContainer<
    RoleAssignmentType extends RoleAssignmentPermissionContext = RoleAssignmentDetailed,
> {
    default_role_assignment: RoleAssignmentType | null;
    other_role_assignments: RoleAssignmentType[];
}

export interface UserProfileDetailedPublicResponse extends UserProfilePublicResponse {
    default_role_assignment: RoleAssignmentPermissionsView | null;
    other_role_assignments: RoleAssignmentPermissionsView[];
}

export function getAllRoleAssignments<RoleAssignmentType extends RoleAssignmentPermissionContext>(
    user: RoleAssignmentsContainer<RoleAssignmentType>
): RoleAssignmentType[] {
    return [
        ...(user.default_role_assignment ? [user.default_role_assignment] : []),
        ...user.other_role_assignments,
    ];
}

/**
 * Request to assign a role to a user
 */
export interface AssignRoleRequest {
    user_uuids: string[];
    role: Role;
    organization_uuid: string | null; // Required for organizational roles, null for global roles
    permissions?: PermissionsGrant[]; // Optional permissions to grant immediately after role assignment creation
}

/**
 * Request to revoke a role from a user
 */
export interface RevokeRoleRequest {
    role_assignment_uuid: string;
    user_uuid: string; // Cross-check for safety
}

/**
 * Request to update a role assignment
 */
export interface UpdateRoleAssignmentRequest {
    role_assignment_uuid: string;
    user_uuid: string; // Cross-check for safety
    role?: Role;
    profile_name?: string; // Deprecated - not supported in current schema
    user_config?: RoleAssignmentCustomConfig;
    physical_terminal_access_expectation?: PhysicalTerminalAccessExpectation;
}

export type UpdateRoleAssignmentBodyRequest = Omit<
    UpdateRoleAssignmentRequest,
    'role_assignment_uuid'
>;

/**
 * Request to delete own account (self-delete)
 */
export interface DeleteMyAccountRequest {
    password: string;
}

/**
 * Response from deleting own account
 */
export interface DeleteMyAccountResponse {
    success: boolean;
    message: string;
}

/**
 * Get profile response (simplified user profile)
 */
export interface GetProfileResponse {
    user: {
        user_uuid: string;
        primary_email: string;
        emails: string[];
        status: Status;
        totp_enabled: boolean;
    } | null;
}

/**
 * Request to update user profile
 */
export interface UpdateProfileRequest {
    primary_email?: string;
    first_name?: string | null;
    last_name?: string | null;
}

/**
 * Request to reset password (admin operation)
 */
export interface ResetPasswordAdminRequest {
    new_password: string;
}

export interface MfaResetRequest {
    reason: string;
    identity_verification_attested: boolean;
}

export interface MfaResetResponse {
    reset_outcome: 'succeeded';
    notification_outcome: 'sent' | 'failed' | 'unavailable';
}

export interface EmailAuthenticationAttemptLimitResetRequest {
    target_user_uuid: string;
}

/**
 * Request to send a password reset email (admin operation)
 */
export interface RequestPasswordResetAdminRequest {
    email: string;
}

/**
 * Request to set the MFA requirement policy for a user.
 */
export interface SetUserMfaRequiredRequest {
    required: boolean;
}

/**
 * Set user emails request (full end state)
 */
export interface SetUserEmailsRequest {
    emails: string[];
    primary_email: string;
}

/**
 * Set user phone numbers request (full end state)
 */
export interface SetUserPhonesRequest {
    phones: string[];
    primary_phone: string | null;
}

export interface RequestAddEmailVerificationRequest {
    email: string;
    skip_verification?: boolean;
}

export interface RequestAddEmailVerificationResponse {
    message: string;
    verification_link?: string;
}

export interface RequestAddPhoneVerificationRequest {
    phone: string;
    skip_verification?: boolean;
}

export interface RequestAddPhoneVerificationResponse {
    message: string;
    delivery_destination?: string;
    expires_at?: string;
}

export interface VerifyAddPhoneRequest {
    phone: string;
    code: string;
}

/**
 * Get emails response
 */
export interface GetEmailsResponse {
    primary_email: string;
    emails: Array<{
        email: string;
        is_primary: boolean;
        is_verified: boolean;
    }>;
}

/**
 * Get my roles response
 */
export interface GetMyRolesResponse {
    roles: Array<{
        role: Role;
        organization_uuid: string | null;
        organization_name?: string;
    }>;
}

/** Immutable physical V1, captured before the approved delivery-uncertainty extension. */
// Discriminator: delivery_outcome
export type LoginInvitationDeliveryV1 =
    | {
          delivery_outcome: 'not_attempted';
      }
    | {
          delivery_outcome: 'provider_accepted';
          attempted_at: string;
      }
    | {
          delivery_outcome: 'failed';
          attempted_at: string;
      };

// Discriminator: activation_state
export type ProvisionedUserV1 =
    | {
          activation_state: 'pending_first_login';
          user_uuid: string;
          first_login_expires_at: string;
          delivery: LoginInvitationDeliveryV1;
      }
    | {
          activation_state: 'active_verification_bypassed';
          user_uuid: string;
          delivery: LoginInvitationDeliveryV1;
      };

export interface LoginInvitationAdministrationStateV1 {
    user_uuid: string;
    user_status: 'PENDING';
    self_service_revoked: boolean;
    current_invitation_expires_at: string | null;
    delivery: LoginInvitationDeliveryV1;
}

export interface ResendAllLoginInvitationsPageV1 {
    results: Array<
        | {
              result_kind: 'resent';
              invitation: LoginInvitationAdministrationStateV1;
          }
        | {
              result_kind: 'failed';
              user_uuid: string;
          }
    >;
    resent_count: number;
    failed_count: number;
    next_cursor?: string;
}

export interface UserProfilePublicResponseV1 {
    user_uuid: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    /** Verified email identities eligible for authentication. */
    emails: string[];
    unverified_emails: string[];
    pending_primary_email: string | null;
    /** Verified phone identities eligible for authentication. */
    phones?: string[];
    unverified_phones: string[];
    primary_phone?: string | null;
    pending_primary_phone: string | null;
    totp_enabled: boolean;
    mfa_required: boolean;
    mfa_email_allowed: boolean;
    mfa_sms_allowed: boolean;
    status: Status;
    login_invitation: LoginInvitationAdministrationStateV1 | null;
    /** Retired bearer field retained only to keep pre-migration readers type-safe. */
    invitation_token?: never;
}

export interface UserProfileDetailedPublicResponseV1 extends UserProfilePublicResponseV1 {
    default_role_assignment: RoleAssignmentPermissionsView | null;
    other_role_assignments: RoleAssignmentPermissionsView[];
}
